import React, { useEffect, useReducer } from 'react';

import { Fade, FormControl, IconButton, InputLabel } from '@material-ui/core';
import { Modal, Select, TextField } from '@material-ui/core';
import { MdScreenRotation } from 'react-icons/md';

import { useAuth } from 'hooks';
import { BASE_URL } from 'consts';

import styles from './Preview.module.scss';

const devices = [
  { name: 'Custom', w: 414, h: 552, custom: true },
  { name: 'Galaxy S5', w: 360, h: 640 },
  { name: 'Pixel 2', w: 411, h: 731 },
  { name: 'Pixel 2 XL', w: 411, h: 823 },
  { name: 'iPhone 5/SE', w: 320, h: 568 },
  { name: 'iPhone 6/7/8', w: 375, h: 667 },
  { name: 'iPhone 6/7/8 Plus', w: 414, h: 736 },
  { name: 'iPhone X', w: 375, h: 812 },
  { name: 'iPad', w: 768, h: 1024 },
  { name: 'iPad Pro', w: 1024, h: 1366 },
];

const reducer = (state, action) => {
  switch (action.type) {
    case 'CHANGE_SIZE':
      return computeProps({
        ...state,
        customSize: { ...state.customSize, ...action.payload },
      });

    case 'CHANGE_SCALE':
      return computeProps({ ...state, scale: action.payload });

    case 'TOGGLE_ROTATE':
      return computeProps({ ...state, rotate: !state.rotate });

    case 'CHANGE_CONTROL_HEIGHT':
      return computeProps({ ...state, controlHeight: action.payload });

    case 'CHANGE_DEVICE': {
      const device = devices[action.payload];

      return device
        ? computeProps({ ...state, selected: action.payload, device, scale: 0 })
        : state;
    }

    case 'REFRESH':
      return computeProps({ ...state });

    case 'CLOSE':
      return { ...state, status: 'closing' };

    default:
      return state;
  }
};

const Preview = props => {
  const [state, dispatch] = useReducer(reducer, 5, selected =>
    computeProps({
      selected,
      device: devices[selected],
      customSize: { w: devices[selected].w, h: devices[selected].h },
    })
  );

  const { open, onClose } = props;
  const auth = useAuth();
  const url = props.url || buildPreviewURL(props, auth);

  useEffect(() => {
    const updateDimensions = () => dispatch({ type: 'CHANGE_STATE' });
    window.addEventListener('resize', updateDimensions);

    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  const handleClick = ({ target }) => {
    if (target.classList && target.classList.contains(styles.container)) {
      props.onClose();
    }
  };

  const renderControls = () => {
    const scale = Math.round(state.scale * 100);

    const custom = state.device.custom;
    const options = [];

    for (let i = 0, j = 50, k = 0; i < 5; ++i, j += 25) {
      if (k < scale && scale < j) {
        options.push(
          <option key={options.length} value={0}>
            {`${scale}%`}
          </option>
        );

        k = scale;
      }

      options.push(
        <option key={options.length} value={j === scale ? 0 : j / 100}>
          {`${j}%`}
        </option>
      );

      k = j;
    }

    return (
      <div
        className={styles.controlBar}
        ref={ref => {
          if (ref) {
            const h = ref.getBoundingClientRect().height;

            if (h !== state.controlHeight) {
              dispatch({ type: 'CHANGE_CONTROL_HEIGHT', payload: h });
            }
          }
        }}
      >
        <FormControl>
          <Select
            native
            value={state.selected}
            onChange={e => dispatch({ type: 'CHANGE_DEVICE', payload: e.target.value })}
            style={{ marginRight: 5 }}
          >
            {devices.map((device, index) => (
              <option key={index} value={index}>
                {device.name}
              </option>
            ))}
          </Select>
        </FormControl>

        <TextField
          className={styles.size}
          value={custom ? state.customSize.w : state.device.w}
          disabled={!custom}
          inputProps={{ style: { textAlign: 'center' }, min: 50 }}
          onChange={e =>
            dispatch({ type: 'CHANGE_SIZE', payload: { w: e.target.value } })
          }
          type="number"
        />

        <InputLabel>x</InputLabel>

        <TextField
          className={styles.size}
          value={custom ? state.customSize.h : state.device.h}
          disabled={!custom}
          inputProps={{ style: { textAlign: 'center' }, min: 50 }}
          onChange={e =>
            dispatch({ type: 'CHANGE_SIZE', payload: { h: e.target.value } })
          }
          type="number"
        />

        <FormControl>
          <Select
            native
            value={0}
            onChange={e => dispatch({ type: 'CHANGE_SCALE', payload: e.target.value })}
            style={{ marginRight: 5 }}
          >
            {options}
          </Select>
        </FormControl>

        <IconButton
          onClick={() => dispatch({ type: 'TOGGLE_ROTATE' })}
          style={{ color: '#000' }}
        >
          <MdScreenRotation style={{ outline: 'none', width: 20, height: 20 }} />
        </IconButton>

        <div style={{ minWidth: 80, textAlign: 'center' }}>
          {computeRatio(custom ? state.customSize : state.device)}
        </div>
      </div>
    );
  };

  return (
    <Modal
      className={styles.preview}
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={open} timeout={500}>
        <div className={styles.container} onClick={handleClick}>
          {renderControls()}

          <div className={styles.webview} style={{ transform: `scale(${state.scale})` }}>
            <object
              aria-label="webview"
              data={url}
              width={state.size.w}
              height={state.size.h}
            />
          </div>
        </div>
      </Fade>
    </Modal>
  );
};

export default Preview;

const computeProps = state => {
  const { device, customSize, rotate, controlHeight } = state;

  let { w, h } = device;

  if (device.custom) {
    w = parseInt(customSize.w);
    h = parseInt(customSize.h);

    if (isNaN(w) || w < 50) {
      w = device.w;
    }

    if (isNaN(h) || h < 50) {
      h = device.h;
    }
  }

  state.size = rotate ? { w: h, h: w } : { w, h };

  let { scale } = state;

  if (!scale && controlHeight) {
    const m2 = 100;
    const maxW = window.innerWidth - m2;
    const maxH = window.innerHeight - m2 - controlHeight - 10;

    const scaleX = w / (maxW || 1);
    const scaleY = h / (maxH || 1);

    if (scaleX > 1 || scaleY > 1) {
      scale = 1 / Math.max(scaleX, scaleY);
    }
  }

  state.scale = scale || 1;

  return state;
};

const computeRatio = ({ w, h }) => {
  w = parseFloat(w);
  h = parseFloat(h);

  let stop = 0;
  let maxIterations = 10;

  const isInteger = value => /^[0-9]+$/.test(value);

  while (!isInteger(w) || !isInteger(h)) {
    w *= 10;
    h *= 10;
    ++stop;

    if (stop > maxIterations) {
      break;
    }
  }

  return reduceRatio(w, h);
};

const reduceRatio = (num, den) => {
  if (num === den) {
    return '1 : 1';
  }

  const gcd = (a, b) => (b ? gcd(b, a % b) : a);

  let temp;

  if (num < den) {
    temp = num;
    num = den;
    den = temp;
  }

  const divisor = gcd(num, den);

  return temp === undefined
    ? `${num / divisor} : ${den / divisor}`
    : `${den / divisor} : ${num / divisor}`;
};

const buildPreviewURL = ({ redirect, type, id }, auth) => {
  if (redirect) {
    return redirect;
  }

  if (type && id && auth && auth.user) {
    return `${BASE_URL}/template/${type}/preview/${id}/${auth.user._id}`;
  }
};
